import {
  BufferGeometry,
  BufferAttribute,
  MeshLambertMaterial,
  Mesh,
  Group,
  SphereGeometry,
  TextureLoader,
  TorusGeometry,
  CylinderGeometry
} from 'three'
import { setOpacity } from '../utilities/utilities'
import ShipBubble from '../textures/shipBubble.jpg'
import ShipTexture from '../textures/spaceshiptexture.jpg'

const COLOR_PALLET = [
  [0.5, 1, 0.5], // 0: Light green.
  [1, 1, 1], // 1: White
  [1, 1, 0.5], // 2: Bright yellow-ish.
  [0, 0.5, 0], // 3. Medium green.
  [0.25, 1, 0.75], // 4: Light cyan-ish.
  [0.5, 0.2, 0], // 5: Dark orange-ish.
  [0, 0.45, 0.65] // 6: dark blue
]

const BODY_VERTICES = [
  [-0.7, 0, 0], // 0: Left middle.
  [0, 0, 0.5], // 1: Front middle.
  [0.7, 0, 0], // 2: Right middle.
  [0, 0, -0.5], // 3: Back middle.
  [0, 2.5, 0], // 4: Top.
  [0, -0.3, 0] // 5: Bottom.
]

const TEXTURE_VERTICES = [
  [0, 0.5],
  [0.5, 0.5],
  [1, 0.5],
  [0.5, 0.5],
  [0.5, 1],
  [0.5, 0]
]

const createSpaceShip = () => {
  const geometry = new BufferGeometry()

  const vertices = new Float32Array([
    ...BODY_VERTICES[0],
    ...BODY_VERTICES[1],
    ...BODY_VERTICES[2],
    ...BODY_VERTICES[3],
    ...BODY_VERTICES[4],
    ...BODY_VERTICES[5]
  ])

  geometry.setAttribute('position', new BufferAttribute(vertices, 3))

  const colors = new Float32Array([
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1],
    ...COLOR_PALLET[1]
  ])

  geometry.setAttribute('color', new BufferAttribute(colors, 3))

  const textureCoords = new Float32Array([
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[3]
  ])

  geometry.setAttribute('uv', new BufferAttribute(textureCoords, 2))

  // prettier-ignore
  geometry.setIndex([
    0,1,4, // Front upper-left quadrant.
    1,2,4, // Front upper-right quadrant.
    0,5,1, // Front bottom-left quadrant.
    1,5,2, // Front bottom-right quadrant.
    0,4,3, // Rear upper-left quadrant (if looking at it from the front).
    3,4,2, // Rear upper-right quadrant.
    0,3,5, // Rear bottom-left quadrant.
    2,5,3 // Rear bottom-right quadrant.
  ])
  // prettier-ignore

  // With every face having its “own” vertex (since vertices are always copied), the computed normals correspond
  // exactly to the faces’ directions, leading to a faceted look.
  geometry.computeVertexNormals()
  return geometry
}

const WING_VERTICES = [
  [0, 0, 0], // 0: flat center
  [0, 1.8, -0.16], // 1: top back.
  [0, 1.8, 0.16], // 2: top front.
  [0, 1.5, 0.5], // 3: front top.
  [0, 1.5, -0.5], // 4: front bottom.
  [0, -0.3, 0.16], // 5: Bottom front.
  [0, -0.3, -0.16], // 6: Bottom back.
  [0, -0.5, 0.5], // 7: back bottom.
  [0, -0.5, -0.5], // 8: back top.
  [0.3, 0, 0] // 9: divot.
]

const createSpaceShipWing = () => {
  const geometry = new BufferGeometry()

  const vertices = new Float32Array([
    ...WING_VERTICES[0],
    ...WING_VERTICES[2],
    ...WING_VERTICES[1], // Left 12 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[1],
    ...WING_VERTICES[4], // Left 11 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[4],
    ...WING_VERTICES[6], // Left 9 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[6],
    ...WING_VERTICES[8], // Left 7 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[8],
    ...WING_VERTICES[7], // Left 6 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[7],
    ...WING_VERTICES[5], // Left 5 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[5],
    ...WING_VERTICES[3], // Left 3 o clock face
    ...WING_VERTICES[0],
    ...WING_VERTICES[3],
    ...WING_VERTICES[2], // Left 1 o clock face

    ...WING_VERTICES[9],
    ...WING_VERTICES[1],
    ...WING_VERTICES[2], // Right 12 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[2],
    ...WING_VERTICES[3], // Right 11 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[3],
    ...WING_VERTICES[5], // Right 9 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[5],
    ...WING_VERTICES[7], // Right 7 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[7],
    ...WING_VERTICES[8], // Right 6 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[8],
    ...WING_VERTICES[6], // Right 5 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[6],
    ...WING_VERTICES[4], // Right 3 o clock face
    ...WING_VERTICES[9],
    ...WING_VERTICES[4],
    ...WING_VERTICES[1] // Right 1 o clock face
  ])

  geometry.setAttribute('position', new BufferAttribute(vertices, 3))

  const colors = new Float32Array([
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6],
    ...COLOR_PALLET[6]
  ])

  geometry.setAttribute('color', new BufferAttribute(colors, 3))

  // With every face having its “own” vertex (since vertices are always copied), the computed normals correspond
  // exactly to the faces’ directions, leading to a faceted look.
  geometry.computeVertexNormals()
  return geometry
}

class Spaceship {
  constructor(boundary) {
    this.boundary = boundary

    this.velocity = { x: 0, y: 0 }
    this.maxVelocity = { x: 0.3, y: 0.3 }
    this.radius = 1
    this.health = 100
    this.cooldown = { shoot: 0, hit: 0, spin: 0 }
    //Credit to filterForger for the ufobubble texture: https://www.filterforge.com/filters/6842.html
    let texture = new TextureLoader()
    const bubbleMaterial = new MeshLambertMaterial({ map: texture.load(ShipBubble) })
    const material = new MeshLambertMaterial({ vertexColors: true })
    //Credit to Eugene Che for the deathstar texture: https://www.123rf.com/photo_8971306_seamless-space-hull-ship-pattern-as-background.html
    const bodyMaterial = new MeshLambertMaterial({ map: texture.load(ShipTexture) })
    const jetMaterial = new MeshLambertMaterial({ color: 0x616161 })
    const jetRingMaterial = new MeshLambertMaterial({ color: 0x80ffbf })
    const bodyGeometry = createSpaceShip()
    const bodyMesh = new Mesh(bodyGeometry, bodyMaterial)
    const wingGeometryL = createSpaceShipWing()
    const wingGeometryR = createSpaceShipWing()
    const leftWingMesh = new Mesh(wingGeometryL, material)
    const rightWingMesh = new Mesh(wingGeometryR, material)
    leftWingMesh.position.x -= 1
    rightWingMesh.position.x += 1
    rightWingMesh.rotateY(Math.PI)
    const bubbleGeometry = new SphereGeometry(0.35, 4, 4)
    const bubbleMesh = new Mesh(bubbleGeometry, bubbleMaterial)
    const jetGeometry = new CylinderGeometry(0.12, 0.12, 0.4, 3)
    const jetMesh1 = new Mesh(jetGeometry, jetMaterial)
    const jetMesh2 = new Mesh(jetGeometry, jetMaterial)
    const jetRingGeo = new TorusGeometry(0.08, 0.06, 20, 20)
    const jetRingMesh1 = new Mesh(jetRingGeo, jetRingMaterial)
    const jetRingMesh2 = new Mesh(jetRingGeo, jetRingMaterial)
    jetRingMesh1.position.x += 0.35
    jetRingMesh1.position.y -= 0.35
    jetRingMesh1.rotation.x += Math.PI / 2
    jetRingMesh2.position.x -= 0.35
    jetRingMesh2.position.y -= 0.35
    jetRingMesh2.rotation.x += Math.PI / 2
    bubbleMesh.position.y += 1.1
    bubbleMesh.position.z += 0.125
    jetMesh1.position.x += 0.35
    jetMesh1.position.y -= 0.1
    jetMesh2.position.x -= 0.35
    jetMesh2.position.y -= 0.1

    this.group = new Group()
    this.group.add(bodyMesh)
    this.group.add(leftWingMesh)
    this.group.add(rightWingMesh)
    this.group.add(bubbleMesh)
    this.group.add(jetMesh1)
    this.group.add(jetMesh2)
    this.group.add(jetRingMesh1)
    this.group.add(jetRingMesh2)
  }

  accelerate(x, y) {
    this.velocity.x = Math.min(this.velocity.x + x, this.maxVelocity.x)
    this.velocity.y = Math.min(this.velocity.y + y, this.maxVelocity.y)
  }

  move() {
    const width = this.boundary.x,
      height = this.boundary.y
    this.velocity.x *= 0.97
    this.velocity.y *= 0.97
    this.group.position.x = Math.max(
      Math.min(this.group.position.x + this.velocity.x, width - this.radius),
      -width + this.radius
    )
    this.group.position.y = Math.max(
      Math.min(this.group.position.y + this.velocity.y, height - this.radius),
      -height + this.radius
    )
    if (Math.abs(this.group.position.x) === width) {
      this.velocity.x = 0
    }
    if (Math.abs(this.group.position.y) === height) {
      this.velocity.y = 0
    }
    this.group.rotation.x = -this.velocity.y + this.cooldown.spin
  }

  updateCooldowns() {
    if (this.cooldown.shoot > 0) {
      this.cooldown.shoot--
    }
    if (this.cooldown.hit > 0) {
      this.cooldown.hit--
      if (this.cooldown.hit === 0) {
        setOpacity(this, 1)
      }
    }
    this.cooldown.spin = Math.abs(this.cooldown.spin) > 0.01 ? this.cooldown.spin * 0.96 : 0
  }
}

export default Spaceship
